import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "../auth";
import { api } from "../api";

interface TenantOpt {
  id: string;
  name: string;
}

/**
 * Superadmin-only tenant picker. Lists every tenant and re-scopes the session
 * to the chosen one via auth.switchTenant (which re-issues the JWT + reloads).
 */
function TenantSwitcher() {
  const { user, switchTenant } = useAuth();
  const [tenants, setTenants] = useState<TenantOpt[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .get<TenantOpt[]>("/api/auth/tenants")
      .then((rows) => {
        if (alive) setTenants(rows);
      })
      .catch(() => {
        /* non-fatal: switcher just stays empty */
      });
    return () => {
      alive = false;
    };
  }, []);

  if (user?.role !== "superadmin") return null;

  return (
    <div className="tenant-switcher">
      <label>Viewing tenant</label>
      <select
        value={user?.tenantId ?? ""}
        disabled={busy}
        onChange={(e) => {
          const id = e.target.value;
          if (!id || id === user?.tenantId) return;
          setBusy(true);
          switchTenant(id).catch(() => setBusy(false));
        }}
      >
        {tenants.length === 0 && <option value="">Loading…</option>}
        {tenants.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </select>
    </div>
  );
}

const TENANT_NAV = [
  { to: "/", label: "Dashboard", end: true },
  { to: "/assistant", label: "Ask ✨" },
  { to: "/review", label: "Review" },
  { to: "/khaata", label: "Ledger" },
  { to: "/transactions", label: "Transactions" },
  { to: "/import", label: "Import" },
  { to: "/vault", label: "Vault" },
  { to: "/assets", label: "Assets" },
  { to: "/loans", label: "Loans" },
  { to: "/incomes", label: "Salary" },
  { to: "/pnl", label: "P&L" },
  { to: "/capgains", label: "Capital Gains" },
  { to: "/tax", label: "Tax / ITR" },
  { to: "/file-return", label: "File Return" },
  { to: "/reminders", label: "Reminders" },
  { to: "/source-of-funds", label: "Source of Funds" },
  { to: "/running-account", label: "Running Account" },
  { to: "/settings", label: "Settings" },
];

const SUPERADMIN_NAV = [{ to: "/settings", label: "Settings", end: true }];

export function NavShell({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  // A superadmin who also owns a tenant (dual-capability, migration 0005)
  // gets the full tenant nav; only a tenant-less superadmin sees the
  // management-only shell.
  const base = user?.role === "superadmin" && !user?.tenantId ? SUPERADMIN_NAV : TENANT_NAV;
  // Superadmins (e.g. Ram) also get the cross-tenant reconciliation screen.
  const items =
    user?.role === "superadmin"
      ? [...base, { to: "/recon", label: "Ram Recon 🔗" }, { to: "/family-tax", label: "Family Tax 🧾" }]
      : base;

  return (
    <div className="app-shell">
      <nav className="app-nav">
        <div className="brand">Xynex Money</div>
        <TenantSwitcher />
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) => (isActive ? "active" : undefined)}
          >
            {item.label}
          </NavLink>
        ))}
        <div className="signout">
          <button type="button" onClick={logout} style={{ width: "100%" }}>
            Sign out {user ? `(${user.email})` : ""}
          </button>
        </div>
      </nav>
      <main className="app-main">{children}</main>
    </div>
  );
}
